import { useState, useEffect, useCallback } from 'react';
import { documentStatusService, DocumentStatusResponse } from '../services/documentStatusService';

export interface UseDocumentStatusReturn {
  status: DocumentStatusResponse | null;
  isLoading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  markForSearch: () => Promise<void>;
  unmarkForSearch: () => Promise<void>;
}

export const useDocumentStatus = (documentId: string | null): UseDocumentStatusReturn => {
  const [status, setStatus] = useState<DocumentStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!documentId)
    {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const documentStatus = await documentStatusService.getDocumentStatus(documentId);
      setStatus(documentStatus);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar status do documento');
      console.error('Erro ao carregar status do documento:', err);
    } finally {
      setIsLoading(false);
    }
  }, [documentId]);

  const refreshStatus = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  const markForSearch = useCallback(async () => {
    if (!documentId)
    {
      return;
    }

    try {
      const updatedStatus = await documentStatusService.markDocumentIndexedForSearch(documentId);
      setStatus(updatedStatus);
    } catch (err: any) {
      setError(err.message || 'Erro ao marcar documento para busca');
      throw err;
    }
  }, [documentId]);

  const unmarkForSearch = useCallback(async () => {
    if (!documentId)
    {
      return;
    }

    try {
      const updatedStatus = await documentStatusService.unmarkDocumentIndexedForSearch(documentId);
      setStatus(updatedStatus);
    } catch (err: any) {
      setError(err.message || 'Erro ao desmarcar documento para busca');
      throw err;
    }
  }, [documentId]);

  useEffect(() => {
    if (!documentId)
    {
      setStatus(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    fetchStatus();
  }, [documentId, fetchStatus]);

  return {
    status,
    isLoading,
    error,
    refreshStatus,
    markForSearch,
    unmarkForSearch,
  };
};
